import React, { useState, useEffect } from 'react';
import { Phone, Mail, ChevronUp, X } from 'lucide-react';
import { useContact } from './context/ContactContext';

/* WhatsApp SVG icon */
const WhatsAppIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/>
  </svg>
);

/* Headset-with-mic SVG icon */
const HeadsetMicIcon = ({ size = 22, className = '' }) => (
  <svg
    width={size} height={size} viewBox="0 0 24 24"
    fill="none" stroke="currentColor" strokeWidth={2}
    strokeLinecap="round" strokeLinejoin="round"
    className={className}
  >
    <path d="M3 11a9 9 0 0 1 18 0" />
    <path d="M21 16a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3v5z" />
    <path d="M3 16a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3v5z" />
    <line x1="12" y1="19" x2="12" y2="22" />
    <path d="M10 22h4" />
  </svg>
);

const FloatingActions = () => {
  const { contactData } = useContact();
  const [showTop, setShowTop] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 300);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const phone    = contactData?.phone || '';
  const email    = contactData?.email || '';
  const whatsapp = contactData?.phone || '';

  const contacts = [
    {
      label: 'Call us',
      icon: Phone,
      color: '#16a34a',
      bg: '#dcfce7',
      href: phone ? `tel:${phone.replace(/\s/g, '')}` : null,
    },
    {
      label: 'WhatsApp',
      icon: WhatsAppIcon,
      color: '#25d366',
      bg: '#d1fae5',
      href: whatsapp ? `https://wa.me/${whatsapp.replace(/[^0-9]/g, '')}` : null,
    },
    {
      label: 'Email',
      icon: Mail,
      color: '#2563eb',
      bg: '#dbeafe',
      href: email ? `mailto:${email}` : null,
    },
  ];

  return (
    <div className="fixed bottom-6 right-5 z-[9990] flex flex-col items-end gap-3  pointer-events-none">

      {/* Speed-dial sub-buttons — fan upward above the FAB row */}
      <div className="flex flex-col items-end gap-2.5">
        {contacts.map(({ label, icon: Icon, color, bg, href }, i) => (
          <div
            key={label}
            className="flex items-center gap-2.5"
            style={{
              opacity: contactOpen ? 1 : 0,
              transform: contactOpen ? 'translateY(0) scale(1)' : 'translateY(16px) scale(0.85)',
              transition: 'opacity 0.3s ease, transform 0.3s ease',
              transitionDelay: contactOpen ? `${i * 60}ms` : `${(contacts.length - 1 - i) * 40}ms`,
              pointerEvents: contactOpen ? 'auto' : 'none',
            }}
          >
            <span className="text-xs font-semibold text-gray-700 bg-white border border-gray-500 lg:border-gray-100 shadow px-2.5 py-1 rounded-full whitespace-nowrap">
              {label}
            </span>
            <a
              href={href || '#'}
              target={href?.startsWith('http') ? '_blank' : undefined}
              rel="noopener noreferrer"
              aria-label={label}
              onClick={e => { if (!href) e.preventDefault(); }}
              className="pointer-events-autow-11 h-11 rounded-full shadow-lg flex items-center justify-center transition-all duration-200 hover:scale-110 active:scale-95 flex-shrink-0"
              style={{ backgroundColor: bg, border: `1.5px solid ${color}33` }}
            >
              <Icon size={20} style={{ color }} strokeWidth={2} />
            </a>
          </div>
        ))}
      </div>

      {/* Bottom row: Go-to-Top arrow + Contact FAB side by side */}
      <div className="flex items-center gap-2">

        {/* Go to Top — slides in beside the FAB when scrolled */}
        <button
          onClick={scrollToTop}
          aria-label="Go to top"
          className={`pointer-events-auto w-11 h-11 rounded-full bg-white border border-gray-200 shadow-lg flex items-center justify-center text-gray-600
            hover:bg-neutral-900 hover:text-white hover:border-neutral-900 transition-all duration-300 ${
            showTop ? 'opacity-100 scale-100' : 'opacity-0 scale-75 pointer-events-none'
          }`}
        >
          <ChevronUp size={18} strokeWidth={2.5} />
        </button>

        {/* Contact FAB */}
        <button
          onClick={() => setContactOpen(o => !o)}
          aria-label={contactOpen ? 'Close contact options' : 'Contact us'}
          className="pointer-events-auto w-13 h-13 rounded-full shadow-xl flex items-center justify-center transition-all duration-300 hover:scale-105 active:scale-95"
          style={{
            width: '52px', height: '52px',
            backgroundColor: contactOpen ? '#1f2937' : '#0f766e',
          }}
        >
          {contactOpen
            ? <X size={22} className="text-white" style={{ transition: 'transform 0.3s', transform: 'rotate(0deg)' }} />
            : <HeadsetMicIcon size={22} className="text-white" />
          }
        </button>

      </div>
    </div>
  );
};

export default FloatingActions;
